import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild, ViewEncapsulation, inject, signal } from '@angular/core';
import { registerScreenActions, registerScreenPanels } from '../../studio-nav/screen-panels.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MembersService } from '../../services/members.service';
import { ClientService } from '../../services/client.service';
import { GlobalVariablesService } from '../../services/global-variables.service';
import { accessLevelFor } from '../../services/access-level-cache.util';
import type { Access } from '../../services/workspaces.util';
import { WorkspacesTabComponent } from './workspaces-tab.component';
import { RolesTabComponent } from './roles-tab.component';
import { PeopleConfirmComponent } from '../../common-components/people-confirm/people-confirm.component';
import { StudioNavService } from '../../studio-nav/studio-nav.service';

/**
 * Users & Workspaces — everything about the people who USE a builder's app.
 *
 * Not to be confused with Team (/dashboard/user-mgmt), which is about the people who BUILD it.
 * That distinction is why this page exists separately and why the nav says what it says.
 */
@Component({
  selector: 'app-users-workspaces',
  standalone: true,
  imports: [CommonModule, FormsModule, WorkspacesTabComponent, RolesTabComponent, PeopleConfirmComponent],
  templateUrl: './users-workspaces.component.html',
  styleUrl: './users-workspaces.component.css',
  // The page's look has to reach the four components rendered INSIDE this one, which emulated
  // encapsulation would stop — so None, with every selector in the stylesheet scoped under `.uw`
  // (the root in the template) instead. That file's header says the rest.
  encapsulation: ViewEncapsulation.None,
})
export class UsersWorkspacesComponent implements OnInit, OnDestroy {
  private members = inject(MembersService);
  private client = inject(ClientService);
  private globalVariablesService = inject(GlobalVariablesService);
  private nav = inject(StudioNavService);

  /** The same flag — and the same toggle — as Pages and Team (`pages.component.ts`), so going from
   *  one of those to this page never flips the lights on the builder. */
  get darkMode(): boolean { return this.globalVariablesService.pageThemeDark; }
  set darkMode(v: boolean) { this.globalVariablesService.pageThemeDark = v; }

  @ViewChild('searchBox') private searchBox?: ElementRef<HTMLInputElement>;

  access = signal<Access>('viewer');
  loadingAccess = signal(true);
  tab = signal<'workspaces' | 'roles'>('workspaces');
  private searchOffer = registerScreenActions({
    'search-workspaces': async (values) => {
      if (this.tab() !== 'workspaces') return { refused: 'The search box is on the Workspaces tab, and the builder is on Roles. Nothing was changed; call navigate first.' };
      const term = String(values['searchTerm'] ?? '').trim();
      // Clearing first drops any keystroke still queued, and nobody is typing here to wait for.
      this.clearSearch();
      this.searchInput = term;
      this.search.set(term);
      return { done: term ? `Searched for ${term}.` : 'Cleared the search.', result: { searchTerm: term } };
    },
  });
  private panels = registerScreenPanels({
    screen: 'workspaces',
    loading: () => !!this.rolesTab?.loading || !!this.workspacesTab?.loading(),
    here: () => ({ tab: this.tab() }),
    holds: () => ({ searchTerm: this.searchInput, ...this.rolesTab?.holds(), ...this.workspacesTab?.holds() }),
    select: (name) => {
      this.tab.set(name as 'workspaces' | 'roles');
      return true;
    },
    unsaved: () => this.rolesTab?.unsavedHere() ?? this.workspacesTab?.unsavedHere() ?? null,
  });
  /** Only the tab on show is drawn, and each form lives in its tab. */
  @ViewChild(RolesTabComponent) private rolesTab?: RolesTabComponent;
  @ViewChild(WorkspacesTabComponent) private workspacesTab?: WorkspacesTabComponent;
  /** What actually reaches `app-workspaces-tab` — and, per Task 11, what triggers its
   *  every-workspace people fetch. Updates 250ms after the LAST keystroke, not the first, so a
   *  search box a builder is still typing into never fires one request per character. */
  search = signal('');
  /** What the input itself shows — updated on every keystroke so typing never feels laggy, even
   *  while `search` above is still debouncing. */
  searchInput = '';
  private searchDebounce: ReturnType<typeof setTimeout> | null = null;
  /** Nav 7b — a destination-note tab switch (e.g. "go to roles"), and a later note while already here. */
  private navSub = this.nav.intent$.subscribe((i) => {
    if (i?.kind === 'tab') { this.nav.take('tab'); this.tab.set(i.tab); }
    if (i?.kind === 'workspace') this.tab.set('workspaces');
  });

  async ngOnInit(): Promise<void> {
    // Nav 7b — the note left by `go()` for the tab to open, taken before anything else runs.
    const t = this.nav.take('tab');
    if (t?.kind === 'tab') this.tab.set(t.tab);
    // Backstop for a typed URL — `LeftNavComponent` already hides the nav item that leads here from
    // a viewer, using the same `accessLevelFor` cache, so on the normal click-through path this
    // resolves instantly from that cache rather than fetching again.
    //
    // myAccessLevel() already resolves to the access string itself (or null for "not a member") —
    // there is no wrapper object to unwrap here.
    const projectId = this.client.selectedClient?.id || this.client.selectedClientId || '';
    try {
      const level = projectId
        ? await accessLevelFor(projectId, () => this.members.myAccessLevel())
        : null;
      this.access.set((level as Access) || 'viewer');
    } catch {
      // A rejected fetch (e.g. a token refresh failure) is never cached (access-level-cache.util.ts
      // drops it so the next read retries) — but THIS visit still needs an answer now rather than
      // hanging on the loading skeleton forever. Fail closed to the same refusal panel a real viewer
      // sees; a reload or a fresh nav click gets a fair retry.
      this.access.set('viewer');
    } finally {
      this.loadingAccess.set(false);
    }
  }

  /** Debounces `search` by 250ms (Task 11, Step 1) — `searchInput` (bound to the input itself)
   *  updates immediately so the box never feels laggy; `search` (what `app-workspaces-tab` actually
   *  reads, and what fires its every-workspace people fetch) only moves once typing pauses. */
  onSearchInput(value: string): void {
    this.searchInput = value;
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
    this.searchDebounce = setTimeout(() => this.search.set(value), 250);
  }

  /** The no-results state's "Clear search" button (Step 3) reaches here via the tab's own
   *  `clearSearch` output. Cancels any pending debounce first — otherwise a timer already queued
   *  from the last keystroke would overwrite this clear a moment later. */
  clearSearch(): void {
    if (this.searchDebounce) { clearTimeout(this.searchDebounce); this.searchDebounce = null; }
    this.searchInput = '';
    this.search.set('');
  }

  /** "/" jumps to the search box from anywhere on the Workspaces tab — the same key most admin tools
   *  use — except while the builder is already typing somewhere, where "/" is just a character. */
  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(e: KeyboardEvent): void {
    if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
    if (this.loadingAccess() || this.access() === 'viewer' || this.tab() !== 'workspaces') return;
    const t = e.target as HTMLElement | null;
    if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
    e.preventDefault();
    this.searchBox?.nativeElement.focus();
  }

  /** Escape inside the box empties it — every workspace comes back — without leaving the box. Goes
   *  through `clearSearch`, so a keystroke's debounce still queued can't undo it a moment later. */
  onSearchKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape' && this.searchInput) {
      e.preventDefault();
      this.clearSearch();
    }
  }

  ngOnDestroy(): void {
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
    // Nav 7b T3 review (I2) — releases the listener this screen took out on `nav.intent$` in its field initialiser.
    this.navSub.unsubscribe();
  }
}
